import React, { useEffect, useState } from 'react'
import { Button, Checkbox } from '@mui/material';

import { ISelectOption } from '../../types/list';

import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';
import Json from '../json';

import '../../styles/shared/multiSelectionManager.scss';

export interface IMultiSelectionManagerProps {
	options: ISelectOption[];
	selectedIds?: number[];
	hideInactive?: boolean;
	title?: string;
	itemType?: string;
	onSelectionChanged?: (selection: number[]) => void;
	onOptionSelected?: (option: ISelectOption) => void;
	onOptionDeselected?: (option: ISelectOption) => void;
	onSelectAll?: (isOrganic: boolean) => void;
	onDeselectAll?: (isOrganic: boolean) => void;
}

interface IWorkingOption {
	id: number;
	option: ISelectOption;
	selected: boolean;
}

const MultiSelectionManager: React.FC<IMultiSelectionManagerProps> = ({ options, selectedIds, hideInactive = true, title, itemType, onSelectionChanged, onOptionSelected, onOptionDeselected, onSelectAll, onDeselectAll }) => {
	const [lastReceivedOptions, setLastReceivedOptions] = useState<ISelectOption[] | undefined>(undefined);
	const [workingOptions, setWorkingOptions] = useState<IWorkingOption[] | undefined>(undefined);

	//#region useEffects
	useEffect(() => {
		if (options != lastReceivedOptions) {
			setLastReceivedOptions(options);
			setWorkingOptions(buildWorkingOptions(options));
		}
	}, [options, selectedIds]);
	//#region useEffects

	//#region Event handlers
	const handleItemCheckBoxClick = (option: IWorkingOption) => {
		toggleOption(option);
	}

	const handleItemRowClick = (option: IWorkingOption, event?: React.MouseEvent<HTMLDivElement>) => {
		if (event.button == 0) {
			event.preventDefault();
			toggleOption(option);
		}
	}

	const handleSelectAllClick = () => {
		getActiveAwareWorkingOptions().forEach(o => o.selected = true);
		setWorkingOptions([...workingOptions]);
		onSelectionChanged?.(workingOptions?.filter(i => i.selected).map(i => i.id) ?? []);
		onSelectAll?.(false);
	}

	const handleDeselectAllClick = () => {
		getActiveAwareWorkingOptions().forEach(o => o.selected = false);
		setWorkingOptions([...workingOptions]);
		onSelectionChanged?.([]);
		onDeselectAll?.(false);
	}
	//#endregion Event handlers

	//#region Action methods
	const buildWorkingOptions = (options: ISelectOption[]): IWorkingOption[] => {
		if (!options || options.length == 0) return [];
		const items = (JSON.parse(JSON.stringify(options)) as ISelectOption[]).map(e => { return { id: +e.value, option: e, selected: selectedIds?.includes(+e.value) ?? false } as IWorkingOption });
		items.sort((a, b) => UtilityService.compareStrings(a.option.name?.toLocaleLowerCase(), b.option.name?.toLocaleLowerCase()));
		return items;
	}

	const toggleOption = (option: IWorkingOption) => {
		option.selected = !option.selected;
		setWorkingOptions([...workingOptions]);
		if (option.selected) {
			onOptionSelected?.(option.option);
		} else {
			onOptionDeselected?.(option.option);
		}
		onSelectionChanged?.(getSelectedWorkingOptions().filter(i => i.selected).map(i => i.id) ?? []);
		if (getUnselectedWorkingCount() == 0) onSelectAll?.(true);
		if (getSelectedWorkingCount() == 0) onDeselectAll?.(true);
	}

	const getActiveAwareWorkingOptions = (): IWorkingOption[] => {
		return workingOptions?.filter(i => !hideInactive || i.option.isActive) ?? [];
	}

	const getSelectedWorkingOptions = (): IWorkingOption[] => {
		return getActiveAwareWorkingOptions()?.filter(i => i.selected && (!hideInactive || i.option.isActive)) ?? [];
	}

	const getUnselectedWorkingOptions = (): IWorkingOption[] => {
		return getActiveAwareWorkingOptions()?.filter(i => !i.selected) ?? [];
	}

	const getSelectedWorkingCount = (): number => {
		return getSelectedWorkingOptions().length;
	}

	const getUnselectedWorkingCount = (): number => {
		return getUnselectedWorkingOptions().length;
	}
	//#endregion Action methods

	return (
		(workingOptions &&
			<div className="multi-selection-manager">
				<div className="header">
					{title &&
						<div className="title">{title}</div>
					}
				</div>
				<div className="table">
					<div className="grid">
						<div className="headers">
							<div className="col-assigned">
								<p>Assigned</p>
							</div>
							<div className="col-name">
								<p>{itemType && `${itemType.trim()} `}Name</p>
								<div className="actions">
									<Button type="button" variant="contained" color="primary" size="small" disabled={getUnselectedWorkingCount() == 0} onClick={handleSelectAllClick}>Select All</Button>
									<Button type="button" variant="contained" color="error" size="small" disabled={getSelectedWorkingCount() == 0} onClick={handleDeselectAllClick}>Clear</Button>
								</div>
							</div>
						</div>
						<div className="body">

							{getActiveAwareWorkingOptions().length > 0 ? (
								<>
									{getActiveAwareWorkingOptions().map((option, index) => (
										<div className="row option" key={index} onMouseDown={(event) => handleItemRowClick(option, event)}>
											<div className="col-assigned">
												<Checkbox
													checked={option.selected}
													disableRipple
													onClick={(event) => { event.stopPropagation(); }}
												></Checkbox>
											</div>
											<div className="col-name">{option.option.name?.trim() ?? `(UNKNOWN NAME, ID ${option.option.value ?? '??'})`}</div>
										</div>
									))}
								</>
							) : (
								<div className="no-results">There are no available {itemType && `${itemType.trim()} `}options to select.</div>
							)}
						</div>
					</div>
				</div>
			</div>
		)
	);
}

export default MultiSelectionManager;